import { OAUTH_NAVIGATION_PATHS, OFFLINE_PAGE_PATH } from './offlinePaths';

export { OFFLINE_PAGE_PATH };
export const OFFLINE_DOCUMENTS_CACHE = 'crm-offline-documents';
export const OFFLINE_FALLBACK_CACHE = 'crm-offline-fallback';

const SUPPORTED_LANGS = new Set(['en', 'es']);

const PUBLIC_NAV_EXACT_SEGMENTS = new Set([
  'register',
  'forgot-password',
  'forgot-location',
  'reset-password',
  'pay',
  'unauthorized',
  'deletion-status',
]);

const PUBLIC_NAV_PREFIX_SEGMENTS = new Set(['login', 'accept-invitation', 'booking', 'required-form']);

/**
 * Distinct from `isPublicPath` in paths.ts. `/offline` is public for auth
 * (no session required) but is not SW-bypassed — the worker serves it from
 * cache. Do not merge the two predicates.
 */
export function isNavigationBypassPath(pathname: string): boolean {
  if (OAUTH_NAVIGATION_PATHS.has(pathname)) return true;

  const parts = pathname.split('/').filter(Boolean);
  if (parts.length < 2) return false;
  const [lang, first] = parts;
  if (!lang || !first || !SUPPORTED_LANGS.has(lang)) return false;
  if (PUBLIC_NAV_PREFIX_SEGMENTS.has(first)) return true;
  return PUBLIC_NAV_EXACT_SEGMENTS.has(first) && parts.length === 2;
}

export function shouldBypassServiceWorker(
  request: Pick<Request, 'url' | 'mode' | 'headers'> & Partial<Pick<Request, 'cache'>>,
  serviceWorkerOrigin?: string
): boolean {
  // DevTools re-reads the page with only-if-cached; re-issuing that from the
  // worker always throws, which Serwist reports as an uncaught no-response.
  if (request.cache === 'only-if-cached' && request.mode !== 'same-origin') {
    return true;
  }

  if (request.headers.get('Accept')?.includes('text/event-stream')) {
    return true;
  }

  const url = new URL(request.url);
  if (request.mode === 'navigate') {
    return isNavigationBypassPath(url.pathname);
  }

  const origin = serviceWorkerOrigin ?? inferServiceWorkerOrigin();
  if (origin && url.origin !== origin) return false;
  if (url.pathname.startsWith('/api-next/auth/')) return true;
  if (url.pathname.startsWith('/api/auth/')) return true;
  if (url.pathname.startsWith('/api/ba/')) return true;
  if (url.pathname === '/api/events') return true;
  return false;
}

function inferServiceWorkerOrigin(): string | undefined {
  if (typeof self !== 'undefined' && 'location' in self) {
    try {
      return self.location.origin;
    } catch {
      return undefined;
    }
  }
  return undefined;
}

/** Only build-owned assets belong in the unencrypted worker cache. */
export function isOfflineAssetPath(pathname: string): boolean {
  return pathname.startsWith('/_next/static/') || /^\/(fonts|assets)\/.*\.(woff2?|css|js|svg|png|webp)$/.test(pathname);
}

export function isLegacyPrivateCache(name: string): boolean {
  return (
    name === OFFLINE_DOCUMENTS_CACHE ||
    /^(apis|others|pages|next-data|next-rsc|next-prefetch|start-url|cross-origin|static-data-assets|static-image-assets|next-image|static-audio-assets|static-video-assets)(-|$)/.test(
      name
    )
  );
}
